import type { StudentLegalHold } from './retention';
export type { StudentLegalHold } from './retention';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Contact details the parent records themselves; absent for anyone predating the fields. */
  phone?: string;
  address?: string;
  isSuperAdmin?: boolean;
  memberships: Membership[];
  createdAt: string;
  updatedAt: string;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  phone?: string;
  email?: string;
  organizationId?: string;
  timezone?: string;
  isActive?: boolean;
  capacity?: number;
  ageGroups?: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Stored lifecycle status of a child. Mirrors `StudentStatus` in
 * src/common/types/student.ts.
 *   PENDING_START — accepted and dated, startDate not yet reached.
 *   ACTIVE        — attending (also "leaving scheduled" with an expectedEndDate).
 *   LEFT          — gone, set explicitly by staff.
 * ARCHIVED is NOT a stored status — it is derived (LEFT + leftAt >12mo) and
 * surfaced by the backend as the `isArchived` flag on each student row.
 */
export enum StudentStatus {
  PENDING_START = 'PENDING_START',
  ACTIVE = 'ACTIVE',
  LEFT = 'LEFT',
}

/**
 * Irish school grades, Pre-school through 6th Class. Mirrors
 * `Grade` in src/common/types/student.ts. Values are the display strings.
 */
export enum Grade {
  PRESCHOOL = 'Pre-school',
  JUNIOR_INFANTS = 'Junior Infants',
  SENIOR_INFANTS = 'Senior Infants',
  FIRST_CLASS = '1st Class',
  SECOND_CLASS = '2nd Class',
  THIRD_CLASS = '3rd Class',
  FOURTH_CLASS = '4th Class',
  FIFTH_CLASS = '5th Class',
  SIXTH_CLASS = '6th Class',
}

export const GRADE_ORDER: readonly Grade[] = [
  Grade.PRESCHOOL,
  Grade.JUNIOR_INFANTS,
  Grade.SENIOR_INFANTS,
  Grade.FIRST_CLASS,
  Grade.SECOND_CLASS,
  Grade.THIRD_CLASS,
  Grade.FOURTH_CLASS,
  Grade.FIFTH_CLASS,
  Grade.SIXTH_CLASS,
];

/**
 * Registration lifecycle for a Student row. Mirrors backend
 * `StudentRegistrationStatus`. Until the parent submits the REGISTRATION
 * form, the row sits at PENDING and the UI gates child-scoped actions.
 */
export type StudentRegistrationStatus = 'PENDING' | 'COMPLETE';

/**
 * A booking for a class term that has not started yet — a forward booking.
 *
 * Deliberately NOT a `Student`: it is an enrolment row, keyed by (student,
 * term), and the same child can hold one of these while also being on today's
 * roster. It carries the `termId` the cancel operation needs.
 */
export interface UpcomingBooking {
  studentId: string;
  firstName?: string;
  lastName?: string;
  grade?: Grade;
  termId: string;
  termName: string;
  termStartDate: string;
  daysOfWeek: number[];
  isFutureBooking: true;
}

/**
 * A row of a class roster (`GET /classes/:classId/students`): the student plus
 * the class-specific facts the roster read adds.
 */
export interface ClassRosterStudent extends Student {
  /** The days of the week this child attends THIS class (0=Sunday..6). */
  daysOfWeek?: number[];
  /**
   * The child's enrolment in this class ends today (removed today after
   * attending). They are still in the class today — the register counts them —
   * but the roster tags them and the Enrol picker still offers them so the
   * removal can be undone. Absent otherwise.
   */
  endsToday?: boolean;
  /** The row's last day (YYYY-MM-DD), when it has one; set alongside `endsToday`. */
  lastDay?: string;
  /**
   * The child starts in this class later in the term (YYYY-MM-DD); not in
   * today's headcount. `daysOfWeek` are the days they will have.
   */
  startsOn?: string;
  /** A change of days scheduled for a later date. */
  upcoming?: { from: string; daysOfWeek: number[] };
}

export interface Student {
  id: string;
  /**
   * firstName / lastName / dateOfBirth / grade may all be empty on creation —
   * the parent fills them via the REGISTRATION form. UI screens that need a
   * name should use the `studentDisplayName` helper, which renders an em-dash
   * placeholder when the names are missing (the row's status badge tells the
   * reader why).
   */
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  locationId: string;
  organizationId?: string;
  parentId: string;
  grade?: Grade;
  status: StudentStatus;
  /** First day attending. In the future while status is PENDING_START. YYYY-MM-DD. */
  startDate?: string;
  /** Planned last day, set when a departure is announced in advance. YYYY-MM-DD. */
  expectedEndDate?: string;
  /** Actual last day, set when status becomes LEFT. YYYY-MM-DD. */
  leftAt?: string;
  /**
   * Server-derived lifecycle flags (spec §2/§3). Present on list/enriched
   * responses; the UI badges and filters off these rather than re-deriving the
   * rules. Optional so callers building a Student literal don't have to set them.
   *   isLeavingScheduled — ACTIVE + expectedEndDate (departure announced).
   *   isArchived         — LEFT + leftAt older than the 12-month quiet period.
   */
  isLeavingScheduled?: boolean;
  isArchived?: boolean;
  /**
   * Retention legal hold (Task 21). When present, this child's retention clocks
   * are FROZEN — the retention overview excludes them and the student page shows
   * a "clocks frozen" note. A hold blocks future disposal; it never deletes.
   */
  legalHold?: StudentLegalHold;
  /**
   * Registration lifecycle. `PENDING` until parent submits REGISTRATION
   * response; backend defaults legacy rows lacking this field to PENDING.
   * Optional in the type to match the backend-input shape; expect a value
   * on every row the API actually returns.
   */
  registrationStatus?: StudentRegistrationStatus;
  dietary?: string;
  secondParent?: {
    name?: string;
    email?: string;
    relationship?: string;
  };
  emergencyContact?: EmergencyContact;
  gp?: { name: string; address?: string; phone: string; audit?: FieldAudit };
  allergies?: { hasAny: boolean; detail?: string; audit?: FieldAudit };
  createdAt: string;
  updatedAt: string;
}

export interface FieldAudit {
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  lastUpdatedByRole: UserType;
}

export interface Membership {
  userId: string;
  /** Set for location-level roles (PARENT, STAFF, LOCATION_ADMIN). Absent for org-level roles. */
  locationId?: string;
  locationName?: string;
  organizationId?: string;
  organizationName?: string;
  role: UserType;
  permissions: Permission[];
  createdAt: string;
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

export interface Permission {
  resource: string;
  action: string;
}

export enum UserType {
  PARENT = 'PARENT',
  STAFF = 'STAFF',
  LOCATION_ADMIN = 'LOCATION_ADMIN',
  ORG_ADMIN = 'ORG_ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

/** Check if a role has admin-level access */
export const isAdminRole = (role: string): boolean =>
  ['LOCATION_ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN'].includes(role);

/** Check if a role has staff-or-above access */
export const isStaffOrAbove = (role: string): boolean =>
  ['STAFF', 'LOCATION_ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN'].includes(role);

/** Check if a role operates at org level (no location context needed) */
export const isOrgLevelRole = (role: string): boolean =>
  ['ORG_ADMIN', 'SUPER_ADMIN'].includes(role);

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  activeMembershipIndex: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface VerificationData {
  email: string;
  verificationCode: string;
}

// Per-class day-level status derived from AttendanceEvents server-side.
// Mirrors backend `DayClassStatus`. Used by the calendar views to colour
// per-day cells.
export enum DayClassStatus {
  HERE = 'HERE',
  GONE = 'GONE',
  PLANNED_ABSENT = 'PLANNED_ABSENT',
  NOT_MARKED = 'NOT_MARKED',
}

export enum ClassInstanceStatus {
  SCHEDULED = 'SCHEDULED',
  CANCELLED = 'CANCELLED',
  MODIFIED = 'MODIFIED',
}

export interface Class {
  id: string;
  name: string;
  locationId: string;
  room?: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  /**
   * Hard per-day enrollment ceiling. The enroll modal renders remaining
   * capacity per running day and prevents picking days that are full.
   */
  capacity: number;
  isActive: boolean;
}

export interface ClassInstance {
  id: string;
  classId: string;
  className: string;
  date: string;
  startTime: string;
  endTime: string;
  status: ClassInstanceStatus;
  cancellationReason?: string;
}

// Calendar Types
export interface CalendarDay {
  date: string;
  dayOfWeek: number;
  classes: CalendarClassEntry[];
}

export interface CalendarClassEntry {
  classId: string;
  className: string;
  classInstanceId?: string;
  startTime: string;
  endTime: string;
  instanceStatus: string;
  attendanceStatus?: DayClassStatus;
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
}

export interface StudentCalendarResponse {
  studentId: string;
  studentName: string;
  locationId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  calendar: CalendarDay[];
}

export interface ClassCalendarDay {
  date: string;
  dayOfWeek: number;
  instance?: {
    id: string;
    status: string;
    startTime: string;
    endTime: string;
  };
  attendance: StudentAttendanceEntry[];
  summary: AttendanceSummary;
}

export interface StudentAttendanceEntry {
  studentId: string;
  studentName: string;
  status: string | null;
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
}

export interface AttendanceSummary {
  totalStudents: number;
  checkedIn: number;
  checkedOut: number;
  absent: number;
  plannedAbsent: number;
  excused: number;
  notMarked: number;
}

export interface ClassCalendarResponse {
  classId: string;
  className: string;
  locationId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalInstances: number;
    cancelledInstances: number;
    averageAttendanceRate: number;
  };
  calendar: ClassCalendarDay[];
}

// ============== Forms Types ==============

export enum FormType {
  REGISTRATION = 'REGISTRATION',
  CONSENT = 'CONSENT',
  STUDENT_INTAKE = 'STUDENT_INTAKE',
}

export enum FormStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum FormOwnerLevel {
  ORGANIZATION = 'ORGANIZATION',
  LOCATION = 'LOCATION',
}

// Keep old enums as aliases for backward compatibility during migration
export const FormWorkflow = FormType;
export type FormWorkflow = FormType;
export const FormTrigger = { POST_REGISTRATION: 'POST_REGISTRATION' as const };
export type FormTrigger = 'POST_REGISTRATION';

export enum FormFieldType {
  STUDENT_SELECTOR = 'STUDENT_SELECTOR',
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  CHECKBOX = 'CHECKBOX',
  CHECKBOX_GROUP = 'CHECKBOX_GROUP',
  RADIO = 'RADIO',
  SELECT = 'SELECT',
  DATE = 'DATE',
  TIME = 'TIME',
  NUMBER = 'NUMBER',
  SIGNATURE = 'SIGNATURE',
  FILE = 'FILE',
  /** Repeating group capturing name + relationship + phone per entry. On
   *  REGISTRATION forms this feeds the student's authorised pickup list
   *  (each entry becomes a PickupContact row on submit). The field is
   *  not required of parents; many will leave it empty. */
  CONTACT_LIST = 'CONTACT_LIST',
}

/**
 * Mirror of `FIELD_TYPE_COMPATIBILITY` from the backend (src/common/types).
 * Keep these in sync — backend rejects incompatible fields with a 400, this
 * is just to keep the form builder honest before we hit the API. See the
 * backend constant for the rationale per form type.
 */
export const FIELD_TYPE_COMPATIBILITY: Record<FormType, FormFieldType[]> = {
  [FormType.REGISTRATION]: [
    FormFieldType.TEXT,
    FormFieldType.TEXTAREA,
    FormFieldType.CHECKBOX,
    FormFieldType.CHECKBOX_GROUP,
    FormFieldType.RADIO,
    FormFieldType.SELECT,
    FormFieldType.DATE,
    FormFieldType.TIME,
    FormFieldType.NUMBER,
    FormFieldType.FILE,
    FormFieldType.SIGNATURE,
    FormFieldType.CONTACT_LIST,
  ],
  [FormType.CONSENT]: [
    FormFieldType.STUDENT_SELECTOR,
    FormFieldType.TEXT,
    FormFieldType.TEXTAREA,
    FormFieldType.CHECKBOX,
    FormFieldType.CHECKBOX_GROUP,
    FormFieldType.RADIO,
    FormFieldType.SELECT,
    FormFieldType.DATE,
    FormFieldType.TIME,
    FormFieldType.NUMBER,
    FormFieldType.FILE,
    FormFieldType.SIGNATURE,
    FormFieldType.CONTACT_LIST,
  ],
  // No STUDENT_SELECTOR — the target student is always known (chosen at
  // enrollment, picked in the Send modal, or via class/location targeting).
  [FormType.STUDENT_INTAKE]: [
    FormFieldType.TEXT,
    FormFieldType.TEXTAREA,
    FormFieldType.CHECKBOX,
    FormFieldType.CHECKBOX_GROUP,
    FormFieldType.RADIO,
    FormFieldType.SELECT,
    FormFieldType.DATE,
    FormFieldType.TIME,
    FormFieldType.NUMBER,
    FormFieldType.FILE,
    FormFieldType.SIGNATURE,
    FormFieldType.CONTACT_LIST,
  ],
};

export function isFieldTypeAllowedForFormType(
  fieldType: FormFieldType,
  formType: FormType
): boolean {
  return FIELD_TYPE_COMPATIBILITY[formType].includes(fieldType);
}

export interface FormFieldValidation {
  min?: number;
  max?: number;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  maxFileSize?: number;
  allowedFileTypes?: string[];
}

/**
 * A field becomes required based on another field's value. Either it must equal
 * a specific value, or it must simply be non-empty — the two forms the builder
 * and evaluator both produce.
 */
export type RequiredWhenRule =
  | {
      /** key of another field in the same form */
      field: string;
      /** required when that field equals this value */
      equals: string | number | boolean;
    }
  | {
      /** key of another field in the same form */
      field: string;
      /** required when that field has any non-empty value */
      notEmpty: true;
    };

export interface FormField {
  id: string;
  /** Stable machine identifier (e.g. "child_first_name"). Used for mapsTo
   *  resolution, requiredWhen references, and response keying. */
  key?: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  /** When true, `required` cannot be changed by admins. */
  requiredLocked?: boolean;
  /** When true, admins can't delete the field or change its type or key. */
  locked?: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validation?: FormFieldValidation;
  order: number;
  /** Dotted path on the Student row to write this answer back to on submit. */
  mapsTo?: string;
  /** Simple conditional-required rule. */
  requiredWhen?: RequiredWhenRule;
}

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  /** When true, admins can't delete or reorder this section. */
  locked?: boolean;
  fields: FormField[];
}

export interface FormDefinition {
  id: string;
  organizationId: string;
  formType: FormType;
  ownerLevel: FormOwnerLevel;
  locationId?: string;
  locationIds?: string[];
  title: string;
  description?: string;
  status: FormStatus;
  sections?: FormSection[];
  targetRoles: string[];
  locationStatuses?: Record<string, FormStatus>;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  // Legacy compat
  workflow?: string;
  trigger?: string;
}

export interface FormVersionSnapshot {
  formId: string;
  version: number;
  fields: FormField[];
  createdBy: string;
  createdAt: string;
}

// ============== Form Distribution ==============

export enum DistributionStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  EXPIRED = 'EXPIRED',
}

export enum DistributionTargetType {
  CLASS = 'CLASS',
  LOCATION = 'LOCATION',
  SPECIFIC_STUDENTS = 'SPECIFIC_STUDENTS',
}

export interface FormDistribution {
  id: string;
  formId: string;
  formTitle: string;
  formVersion: number;
  organizationId: string;
  locationId: string;
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  targetType: DistributionTargetType;
  targetId?: string;
  targetStudentIds?: string[];
  deadline?: string;
  isBlocking: boolean;
  status: DistributionStatus;
  sentBy: string;
  sentByName: string;
  sentAt: string;
  totalInvitations: number;
  completedCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============== Form Responses ==============

export enum FormResponseStatus {
  SUBMITTED = 'SUBMITTED',
  REVIEWED = 'REVIEWED',
  REQUIRES_ACTION = 'REQUIRES_ACTION',
}

export interface FormFileAttachment {
  fieldId: string;
  fileId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key: string;
}

export interface FormResponse {
  id: string;
  formId: string;
  formTitle: string;
  formVersion: number;
  distributionId?: string;
  studentId: string;
  studentName: string;
  locationId: string;
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  responses: Record<string, unknown>;
  files?: FormFileAttachment[];
  status: FormResponseStatus;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ============== Form Invitations ==============

export enum FormInvitationStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
}

export interface FormInvitation {
  id: string;
  formId: string;
  formTitle: string;
  distributionId?: string;
  studentId: string;
  studentName: string;
  locationId: string;
  parentId: string;
  parentName: string;
  status: FormInvitationStatus;
  isBlocking: boolean;
  deadline?: string;
  eventName?: string;
  sentBy: string;
  sentByName: string;
  sentAt: string;
  completedAt?: string;
  responseId?: string;
  createdAt: string;
  updatedAt: string;
}

// ============== Messaging Types ==============

export enum MessageType {
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  PARENT_DM = 'PARENT_DM',
}

export enum MessageTargetType {
  ORG = 'ORG',
  LOCATION = 'LOCATION',
  CLASS = 'CLASS',
  STUDENTS = 'STUDENTS',
  STUDENT_PARENTS = 'STUDENT_PARENTS',
}

export enum MessageStatus {
  SENT = 'SENT',
  WITHDRAWN = 'WITHDRAWN',
}

export enum MessageDeliveryStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
}

export enum PushDeliveryStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export interface Message {
  id: string;
  type: MessageType;
  senderId: string;
  senderName: string;
  senderRole: UserType;
  title: string;
  body: string;
  targetType: MessageTargetType;
  targetOrgId?: string;
  targetLocationId?: string;
  targetClassId?: string;
  targetStudentIds?: string[];
  /** For PARENT_DM only: the student this DM is about. */
  studentId?: string;
  recipientCount: number;
  /** Whether parents may answer this message (opens a thread back to the sender). */
  repliesAllowed: boolean;
  /** Threads parents have opened in reply to this message. */
  replyCount: number;
  status: MessageStatus;
  withdrawnAt?: string; // ISO string (backend Date serialised over the wire)
  withdrawnBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageDelivery {
  id: string;
  messageId: string;
  recipientUserId: string;
  recipientRole: UserType;
  /** Denormalised so inbox queries never need a Message fetch. */
  type: MessageType;
  title: string;
  bodyPreview: string;
  /** Full message text. Absent on deliveries stored before it was carried,
   *  which show the preview only. */
  body?: string;
  senderName: string;
  senderRole: UserType;
  studentId?: string;
  studentName?: string;
  /** For thread replies only: the thread this delivery links back to. */
  threadId?: string;
  /** Copied from the Message. Absent on deliveries stored before replies existed — read as no replies. */
  repliesAllowed?: boolean;
  targetLocationId?: string;
  targetClassId?: string;
  status: MessageDeliveryStatus;
  readAt?: string; // ISO string
  pushStatus?: PushDeliveryStatus;
  pushError?: string;
  createdAt: string;
  updatedAt: string;
}

// ============== Two-way threads (parent <-> staff) ==============

/**
 * A thread is OPEN while the parent's latest post is unanswered, and flips to
 * ANSWERED once a staff/admin posts into it. The backend enforces at most one
 * OPEN thread per child (anti-spam), which is why `startThread` can 409.
 */
export enum ThreadStatus {
  OPEN = 'OPEN',
  ANSWERED = 'ANSWERED',
}

/** Which side of the conversation a message came from. */
export type ThreadSide = 'PARENT' | 'STAFF';

/**
 * Wire shape of a thread. Mirrors the backend `Thread` (src/common/types/thread.ts)
 * with Dates as ISO strings — `ui/` is a separate TS project and never imports
 * server types.
 */
export interface Thread {
  id: string;
  studentId: string;
  locationId: string;
  organizationId: string;
  status: ThreadStatus;
  startedByUserId: string;
  startedByName: string;
  lastMessageAt: string; // ISO string (backend Date serialised over the wire)
  lastMessageBy: ThreadSide;
  lastMessagePreview: string;
  messageCount: number;
  /** Set when the thread was opened as a reply to a staff message. */
  originMessageId?: string;
  originTitle?: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}

/** Wire shape of one message inside a thread. Ordered oldest-first by the API. */
export interface ThreadMessage {
  id: string;
  threadId: string;
  studentId: string;
  locationId: string;
  senderId: string;
  senderName: string;
  senderRole: UserType;
  senderSide: ThreadSide;
  body: string;
  createdAt: string; // ISO string
}

/**
 * 409 body returned by `POST /students/:studentId/threads` when the child
 * already has an OPEN thread. The UI routes the parent into `threadId` rather
 * than showing a hard error.
 */
export interface ThreadAlreadyOpenError {
  error: 'conflict';
  reason: 'THREAD_ALREADY_OPEN';
  threadId: string;
}

// ============== Observations ==============

export enum ObservationVisibility {
  STAFF_ONLY = 'STAFF_ONLY',
  PARENTS = 'PARENTS',
}

export enum ObservationStatus {
  ACTIVE = 'ACTIVE',
  WITHDRAWN = 'WITHDRAWN',
}

export interface ObservationPhoto {
  s3Key: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
}

export interface Observation {
  id: string;
  sessionId: string;
  classId: string;
  locationId: string;
  organizationId: string;
  date: string; // YYYY-MM-DD
  studentId: string;
  studentName: string;
  authorId: string;
  authorName: string;
  authorRole: UserType;
  text: string;
  photos: ObservationPhoto[];
  learningGoalIds?: string[];
  visibility: ObservationVisibility;
  status: ObservationStatus;
  withdrawnAt?: string;
  withdrawnBy?: string;
  editedAt?: string;
  editedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ObservationDelivery {
  id: string;
  observationId: string;
  recipientUserId: string;
  recipientRole: UserType;
  studentId: string;
  studentName: string;
  sessionId: string;
  classId: string;
  date: string;
  authorName: string;
  textPreview: string;
  photoCount: number;
  firstPhotoKey?: string;
  status: MessageDeliveryStatus;
  readAt?: string;
  pushStatus?: PushDeliveryStatus;
  pushError?: string;
  createdAt: string;
  updatedAt: string;
}

/** Subset returned by GET /sessions/:sessionId/observations (staff feed). */
export interface SessionObservationSummary {
  id: string;
  studentId: string;
  studentName: string;
  authorName: string;
  text: string;
  photoCount: number;
  visibility: ObservationVisibility;
  status: ObservationStatus;
  createdAt: string;
}

// ============== Moments (photo moments + parent feed) ==============

export interface MomentPhoto {
  s3Key: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
}

/** POST /moments request body. */
export interface CreateMomentInput {
  sessionId: string;
  studentId: string;
  caption?: string;
  photos: MomentPhoto[];
}

/** Full moment record (as returned by POST /moments and GET /moments/:id). */
export interface Moment {
  id: string;
  sessionId: string;
  studentId: string;
  studentName: string;
  caption?: string;
  photos: MomentPhoto[];
  authorName: string;
  hidden: boolean;
  createdAt: string;
}

/**
 * Staff projection returned by GET /sessions/:sessionId/moments — carries a
 * `photoCount`, NOT the raw `photos[]` (no presigned URLs at the list level).
 */
export interface SessionMomentSummary {
  id: string;
  studentId: string;
  studentName: string;
  authorName: string;
  caption?: string;
  photoCount: number;
  createdAt: string;
}

/**
 * Parent moments-only projection returned by GET /me/children/:childId/moments
 * (momentService.listForStudent). Hidden moments are dropped server-side and
 * photos are presigned into `photoUrls` — so this is NOT the full Moment shape.
 */
export interface ChildMomentSummary {
  id: string;
  caption?: string;
  authorName: string;
  createdAt: string;
  photoUrls: string[];
}

/** Merged parent "Recent feed" item — a moment or an observation. */
export interface FeedItem {
  kind: 'MOMENT' | 'OBSERVATION';
  id: string;
  createdAt: string;
  authorName: string;
  text?: string;
  photoUrls: string[];
}

// ============== Fire Drills ==============

export enum FireDrillType {
  ANNOUNCED = 'ANNOUNCED',
  UNANNOUNCED = 'UNANNOUNCED',
  EVAC_REAL = 'EVAC_REAL',
}

export enum FireDrillStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum FireDrillScope {
  LOCATION = 'LOCATION',
  CLASS = 'CLASS',
}

export enum FireDrillIssueCategory {
  DOOR = 'DOOR',
  ALARM = 'ALARM',
  ROUTE = 'ROUTE',
  STAFF = 'STAFF',
  CHILD = 'CHILD',
  EQUIPMENT = 'EQUIPMENT',
  OTHER = 'OTHER',
}

export interface FireDrillAttendee {
  studentId: string;
  studentName: string;
  sessionId: string;
  accountedFor: boolean;
  unaccountedReason?: string;
  secondsToAccount?: number;
}

export interface FireDrillStaffPresent {
  userId: string;
  userName: string;
  role: UserType;
}

export interface FireDrillIssue {
  id: string;
  category: FireDrillIssueCategory;
  description: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedByName?: string;
  resolutionNote?: string;
}

export interface FireDrillEditEntry {
  editedAt: string;
  editedBy: string;
  editedByName: string;
  fields: string[];
}

export interface FireDrill {
  id: string;
  locationId: string;
  organizationId: string;
  type: FireDrillType;
  scope: FireDrillScope;
  classId?: string;
  className?: string;
  startedAt: string;
  startedBy: string;
  startedByName: string;
  isLiveRecording: boolean;
  completedAt?: string;
  evacuationSeconds?: number;
  assemblyPoint: string;
  exitRoute: string;
  scenario?: string;
  status: FireDrillStatus;
  attendees: FireDrillAttendee[];
  staff: FireDrillStaffPresent[];
  headcountReconciled: boolean;
  notes?: string;
  issues: FireDrillIssue[];
  signedOffBy?: string;
  signedOffByName?: string;
  signedOffAt?: string;
  withdrawnAt?: string;
  withdrawnBy?: string;
  edits?: FireDrillEditEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface FireDrillCadence {
  monthLabel: string;
  daysInMonthRemaining: number;
  isCompliant: boolean;
  classesNotDrilledThisMonth: { id: string; name: string }[];
  lastDrillAt: string | null;
  daysSinceLastDrill: number | null;
}

// ============== Cleaning Schedules ==============

export type CleaningFrequency = 'DAILY' | 'TWICE_DAILY' | 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY';

export type CleaningAreaStatus = 'OK' | 'DUE' | 'OVERDUE';

export interface CleaningArea {
  id: string;
  locationId: string;
  organizationId: string;
  name: string;
  /** Optional room/zone label used to group the list. Free text, not an entity. */
  zone?: string;
  frequency: CleaningFrequency;
  isHighRisk: boolean;
  notes?: string;
  isActive: boolean;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CleaningCompletion {
  /** Room snapshot at completion time — see the backend type. */
  areaZone?: string;
  id: string;
  areaId: string;
  areaName: string;
  locationId: string;
  cleanedBy: string;
  cleanedByName: string;
  cleanedAt: string;
  note?: string;
  productUsed?: string;
}

export type CleaningAreaWithStatus = CleaningArea & {
  status: CleaningAreaStatus;
  lastCleanedAt?: string;
  lastCleanedByName?: string;
  /** Note filed with the most recent sign-off. */
  lastNote?: string;
  /** Completions filed in the current window. Absent from responses served by
   *  a backend older than this field — treat undefined as 0. */
  completionsInWindow?: number;
};

// ============== Learning Goals (Aistéar) ==============

export type LearningGoalFramework = 'AISTEAR_2024' | 'AISTEAR_2009' | 'CUSTOM';

export type LearningGoalTheme =
  | 'WELLBEING'
  | 'IDENTITY_BELONGING'
  | 'COMMUNICATING'
  | 'EXPLORING_THINKING';

export interface LearningGoal {
  id: string;
  framework: LearningGoalFramework;
  organizationId: string | null;
  theme: LearningGoalTheme;
  aimNumber: number;
  aimText: string;
  goalIndex: number;
  text: string;
  isActive: boolean;
}

/** A linked goal as resolved server-side by GET /observations/:id. */
export type ObservationLearningGoal = Pick<
  LearningGoal,
  'id' | 'theme' | 'aimNumber' | 'aimText' | 'goalIndex' | 'text'
>;

export interface CoverageGoal {
  goalId: string;
  theme: LearningGoalTheme;
  aimNumber: number;
  goalIndex: number;
  text: string;
  observationCount: number;
  lastTaggedAt?: string;
  observationIds: string[];
}

export interface LearningGoalCoverage {
  eligible: boolean;
  coverage: CoverageGoal[];
}

// ============== Incidents (parent-facing) ==============
//
// These mirror ONLY the parent-safe allowlist projection returned by the
// backend (`src/incidents/utils/parent-projection.ts`), not the full
// `Incident` record. There is deliberately NO `customFields`, no witness
// identity, no Tusla fields, and no `siblings` — the backend never sends
// them to a parent, so the UI must never be built expecting them.

export enum IncidentSeverity {
  MINOR = 'MINOR',
  SIGNIFICANT = 'SIGNIFICANT',
  NOTIFIABLE = 'NOTIFIABLE',
}

export enum IncidentStatus {
  SUBMITTED = 'SUBMITTED',
  CLOSED = 'CLOSED',
}

export enum IncidentType {
  ACCIDENT = 'ACCIDENT',
  INJURY = 'INJURY',
  ILLNESS = 'ILLNESS',
  MEDICATION_ERROR = 'MEDICATION_ERROR',
  BEHAVIOURAL = 'BEHAVIOURAL',
  OTHER = 'OTHER',
}

export interface ParentIncidentAttachment {
  s3Key: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  /** Presigned, short-lived URL — present on the detail response only. */
  url?: string;
}

/** Parent-safe projection of an incident — the allowlist, and nothing more. */
export interface ParentIncident {
  id: string;
  childId: string;
  childName: string;
  incidentType: IncidentType;
  occurredAt: string;
  locationDetail: string;
  description: string;
  injuryDetail?: string;
  actionTaken: string;
  followUp?: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  submittedAt?: string;
  closedAt?: string;
  reportedByStaffName: string;
  attachments: ParentIncidentAttachment[];
}

/** POST /me/incidents/{incidentId}/acknowledge response's delivery shape,
 *  trimmed to what the parent UI actually reads. */
export interface IncidentDelivery {
  id: string;
  incidentId: string;
  childId: string;
  childName: string;
  acknowledgedAt?: string;
}

// ============== Incidents (staff-facing) ==============
//
// The FULL record, as staff/admin can see it — a strict superset of
// ParentIncident above (adds witness identity, Tusla sub-record, customFields,
// DRAFT/WITHDRAWN statuses). Kept as separate types rather than widening
// ParentIncident because the backend never sends these fields to a parent —
// mixing the two shapes would let a UI accidentally assume a field is present
// on the parent-safe projection when it isn't.

/** Statutory categories under S.I. 575/2018. Mirrors
 *  `src/common/types/incident.ts`'s NotifiableCategory server-side. */
export enum NotifiableCategory {
  SERIOUS_INJURY_HOSPITAL = 'SERIOUS_INJURY_HOSPITAL',
  INFECTIOUS_DISEASE = 'INFECTIOUS_DISEASE',
  CHILD_MISSING = 'CHILD_MISSING',
  UNPLANNED_CLOSURE = 'UNPLANNED_CLOSURE',
  DEATH = 'DEATH',
  OTHER_NOTIFIABLE = 'OTHER_NOTIFIABLE',
}

/** Staff-facing incident status — a superset of the parent-visible
 *  IncidentStatus (which only ever shows SUBMITTED/CLOSED). */
export enum StaffIncidentStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  CLOSED = 'CLOSED',
  WITHDRAWN = 'WITHDRAWN',
}

export interface StaffIncidentAttachment {
  s3Key: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  /** Presigned, short-lived URL — present on the detail response only
   *  (`GET /incidents/{incidentId}` presigns each attachment server-side;
   *  the register/list response does not). Optional because
   *  `CreateIncidentInput.attachments` shares this type for the upload
   *  payload, where no URL exists yet. */
  url?: string;
}

/** Full incident record as returned to staff/admin by POST /incidents and
 *  GET /locations/{locationId}/incidents. */
export interface StaffIncident {
  id: string;
  locationId: string;
  organizationId: string;
  childId: string;
  childName: string;
  incidentGroupId?: string;
  /** Session this was filed from, if any (see CreateIncidentInput.sessionId). */
  sessionId?: string;
  incidentType: IncidentType;
  occurredAt: string;
  locationDetail: string;
  description: string;
  injuryDetail?: string;
  actionTaken: string;
  reportedByStaffId: string;
  reportedByStaffName: string;
  witnessStaffId?: string;
  witnessStaffName?: string;
  severity: IncidentSeverity;
  notifiableCategory?: NotifiableCategory;
  followUp?: string;
  attachments: StaffIncidentAttachment[];
  customFields: Record<string, unknown>;
  status: StaffIncidentStatus;
  submittedAt?: string;
  closedAt?: string;
  withdrawnAt?: string;
  withdrawnBy?: string;
  withdrawnReason?: string;
  tuslaDeadlineAt?: string;
  tuslaReportedAt?: string;
  tuslaReportedBy?: string;
  tuslaReference?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/** Append-only correction trail entry, as returned by
 *  `GET /incidents/{incidentId}`'s `amendments` array. Mirrors
 *  `IncidentAmendment` server-side (`src/common/types/incident.ts`) — a
 *  discriminated union on `kind`, so the register/detail UI can render each
 *  kind distinctly rather than a generic "amendment" bullet. */
export type IncidentAmendment = {
  amendmentId: string;
  authorId: string;
  authorName: string;
  createdAt: string;
} & (
  | { kind: 'NOTE'; text: string }
  | {
      kind: 'SEVERITY_CHANGE';
      from: IncidentSeverity;
      to: IncidentSeverity;
      reason: string;
      reopenedFromClosed: boolean;
      clearedTuslaReport: boolean;
    }
  | { kind: 'TUSLA_REPORTED'; reportedAt: string; reference: string }
  | { kind: 'WITHDRAWN'; reason: string }
);

/** One other child's record from the same group event — staff-only, as
 *  returned by `GET /incidents/{incidentId}`'s `siblings` array. Parents
 *  never see this (the parent projection omits `incidentGroupId` entirely). */
export interface IncidentSibling {
  id: string;
  childName: string;
}

/** Staff-facing per-guardian delivery row — a strict superset of the
 *  parent-safe `IncidentDelivery` above (adds recipientUserId, status,
 *  notifiedAt, push status). Kept as a separate type for the same reason
 *  StaffIncident is kept separate from ParentIncident: the backend never
 *  sends these fields anywhere but the staff detail response. */
export interface StaffIncidentDelivery {
  id: string;
  incidentId: string;
  recipientUserId: string;
  childId: string;
  childName: string;
  status: MessageDeliveryStatus;
  notifiedAt: string;
  acknowledgedAt?: string;
  pushStatus?: PushDeliveryStatus;
  pushError?: string;
}

/** GET /incidents/{incidentId} response — the full staff detail payload. */
export interface IncidentDetailResponse {
  incident: StaffIncident;
  amendments: IncidentAmendment[];
  siblings: IncidentSibling[];
  deliveries: StaffIncidentDelivery[];
}

/** Filters accepted by both GET /locations/{locationId}/incidents (the
 *  register) and GET /students/{studentId}/incidents (per-child staff
 *  history) — pushed straight through as query params so DynamoDB, not the
 *  client, does the filtering. `childId` is register-only (the per-child
 *  endpoint is already scoped to one student). */
export interface IncidentRegisterFilters {
  fromDate?: string;
  toDate?: string;
  severity?: IncidentSeverity;
  incidentType?: IncidentType;
  status?: StaffIncidentStatus;
  childId?: string;
}

/** POST /incidents request body. `submitNow: true` creates and submits in
 *  ONE round trip — the fast path this whole feature exists for. */
export interface CreateIncidentInput {
  incidentId?: string;
  childId: string;
  locationId: string;
  organizationId: string;
  incidentGroupId?: string;
  /** Optional link to the live session this was filed from (RunSession roster).
   *  The backend validates it belongs to the same location before storing. */
  sessionId?: string;
  incidentType?: IncidentType;
  occurredAt?: string;
  locationDetail?: string;
  description?: string;
  injuryDetail?: string;
  actionTaken?: string;
  witnessStaffId?: string;
  severity?: IncidentSeverity;
  notifiableCategory?: NotifiableCategory;
  followUp?: string;
  attachments?: StaffIncidentAttachment[];
  customFields?: Record<string, unknown>;
  submitNow: boolean;
}

/**
 * Activity roll-up for a single session (GET /sessions/:id/summary). Counts of
 * what staff recorded against the session — surfaced on RunSession so a running
 * or ended session shows more than a bare roster.
 */
export interface SessionSummary {
  sessionId: string;
  status: string;
  presentCount: number;
  /** Register-style attendance breakdown across the expected roster. Most
   *  useful on an ended session's recap. `noShow` only counts once the session
   *  has ended; while live, not-yet-arrived children are `stillExpected`. */
  attendance?: {
    expected: number;
    present: number;
    leftEarly: number;
    plannedAbsent: number;
    noShow: number;
    stillExpected: number;
  };
  observationCount: number;
  momentCount: number;
  incidentCount: number;
  medsGiven: number;
  medsDue: number;
}

// ============== Incidents — per-org custom fields (Task 24) ==============
//
// Mirrors `src/common/types/incident.ts` server-side. Deliberately a narrow
// allowlist, NOT the full FormFieldType vocabulary — see that file's doc
// comment for why STUDENT_SELECTOR/SIGNATURE/file-upload are excluded.

export enum IncidentCustomFieldType {
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  SELECT = 'SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
}

export interface IncidentCustomFieldDef {
  key: string;
  type: IncidentCustomFieldType;
  label: string;
  required: boolean;
  options?: string[];
  order: number;
}

/** GET/PUT /organizations/{organizationId}/incident-fields payload. */
export interface IncidentFieldsConfig {
  organizationId: string;
  fields: IncidentCustomFieldDef[];
  updatedAt: string | null;
  updatedBy: string | null;
}

// ============== Medication Requirements & Administration Log ==============
//
// Mirrors `src/common/types/medication.ts` server-side. The UI does not
// cross-import from `src/common` (see the `Grade` comment at
// `src/common/types/student.ts:37`), so these are hand-copied rather than
// imported.

export enum MedicationRequirementType {
  STANDING = 'STANDING',
  ONE_OFF = 'ONE_OFF',
  EMERGENCY_ONLY = 'EMERGENCY_ONLY',
}

export enum MedicationRoute {
  ORAL = 'ORAL',
  INHALED = 'INHALED',
  TOPICAL = 'TOPICAL',
  INJECTION = 'INJECTION',
  OTHER = 'OTHER',
}

export enum MedicationRequirementStatus {
  PENDING_CONSENT = 'PENDING_CONSENT',
  ACTIVE = 'ACTIVE',
  ENDED = 'ENDED',
  REVOKED = 'REVOKED',
  EXPIRED_MEDICATION = 'EXPIRED_MEDICATION',
}

export enum MedicationEntryType {
  ADMINISTERED = 'ADMINISTERED',
  REFUSED = 'REFUSED',
  MISSED = 'MISSED',
  PARTIAL = 'PARTIAL',
  CORRECTION = 'CORRECTION',
  EMERGENCY_USE = 'EMERGENCY_USE',
}

/**
 * N discrete administration times per day. `daysOfWeek` mirrors the same
 * `number[]` (0=Sunday) shape used for `Class.daysOfWeek` elsewhere in this
 * file — no separate `DayOfWeek` enum is mirrored here.
 */
export interface MedicationSchedule {
  /** "HH:MM", 24h. At least one. */
  timesOfDay: string[];
  daysOfWeek: number[];
}

export interface MedicationConsent {
  confirmedAt?: string;
  confirmedByUserId?: string;
  /** Denormalised at consent time — the printed record needs a name, not a
   *  uuid. Absent on rows consented before this field existed. */
  confirmedByName?: string;
  revokedAt?: string;
  revokedByUserId?: string;
  revokedReason?: string;
}

/** The full, staff-facing shape. See `MedicationRequirement` doc comment
 *  server-side for why each field exists. */
export interface MedicationRequirement {
  requirementId: string;
  childId: string;
  locationId: string;
  organizationId: string;
  medicationName: string;
  requirementType: MedicationRequirementType;
  dosage: string;
  route: MedicationRoute;
  /** Null iff EMERGENCY_ONLY. */
  schedule: MedicationSchedule | null;
  /** Required iff EMERGENCY_ONLY. */
  emergencyInstructions?: string;
  prescriber?: string;
  startDate: string;
  endDate?: string;
  medicationExpiryDate?: string;
  specialInstructions?: string;
  attachments: string[];
  status: MedicationRequirementStatus;
  consent: MedicationConsent;
  needsScheduleReview?: boolean;
  consentRequestedAt?: string;
  endedAt?: string;
  endedByUserId?: string;
  endedReason?: string;
  createdAt: string;
  createdBy: string;
  createdByRole: 'PARENT' | 'STAFF';
  updatedAt: string;
}

export interface MedicationAdministrationEntry {
  entryId: string;
  requirementId: string;
  childId: string;
  locationId: string;
  organizationId: string;
  entryType: MedicationEntryType;
  administeredAt: string;
  doseGiven: string;
  administeredByStaffId: string;
  administeredByName: string;
  witnessStaffId?: string;
  witnessStaffName?: string;
  unwitnessedReason?: string;
  correctsEntryId?: string;
  notes?: string;
  overrideReason?: string;
  scheduledSlot?: string;
  administeredPastExpiry?: boolean;
  sessionId?: string;
  createdAt: string;
}

/**
 * The staff view of one requirement + its full log, as returned by
 * `GET /students/{studentId}/medications` and
 * `GET /medications/{medicationId}/administrations`.
 */
export interface MedicationRequirementWithEntries extends MedicationRequirement {
  entries: MedicationAdministrationEntry[];
}

/**
 * The parent-safe allowlist projection returned by
 * `GET /me/children/{childId}/medications`. A 19-key allowlist — 18
 * requirement-level keys plus `entries` — deliberately NOT
 * `MedicationRequirement`: it omits `locationId`, `organizationId`,
 * `createdBy`, `consentRequestedAt`, `endedAt`/`endedByUserId`/`endedReason`,
 * and `updatedAt`, and it OMITS `attachments` (see
 * `src/medications/utils/parent-projection.ts` — there is no medication
 * upload purpose, so no key in that field can have come from the presign
 * flow, and emitting it would be the read half of a cross-tenant vector).
 * Do not add `attachments` here.
 */
export interface ParentSafeMedicationEntry {
  entryId: string;
  entryType: MedicationEntryType;
  administeredAt: string;
  doseGiven: string;
  administeredByName: string;
  witnessStaffName?: string;
  unwitnessedReason?: string;
  notes?: string;
  scheduledSlot?: string;
  administeredPastExpiry?: boolean;
  correctsEntryId?: string;
  createdAt: string;
}

export interface ParentSafeMedication {
  requirementId: string;
  childId: string;
  medicationName: string;
  requirementType: MedicationRequirementType;
  dosage: string;
  route: MedicationRoute;
  schedule: MedicationSchedule | null;
  emergencyInstructions?: string;
  prescriber?: string;
  startDate: string;
  endDate?: string;
  medicationExpiryDate?: string;
  specialInstructions?: string;
  status: MedicationRequirementStatus;
  consent: MedicationConsent;
  needsScheduleReview?: boolean;
  createdAt: string;
  createdByRole: 'PARENT' | 'STAFF';
  entries: ParentSafeMedicationEntry[];
}

/** One scheduled slot on the "due today" board. */
export interface MedicationDueSlot {
  requirementId: string;
  childId: string;
  medicationName: string;
  dosage: string;
  /** "HH:MM" in Dublin. */
  slot: string;
  state: 'DUE' | 'GIVEN' | 'OVERDUE' | 'REFUSED' | 'MISSED';
  /** Set (to the expiry date) when the medicine has PASSED its expiry — the
   *  server refuses a routine dose from that day on, so the board renders
   *  this as expired, never as an actionable "give". */
  medicationExpiredOn?: string;
}

/** A passive EMERGENCY_ONLY reminder on the "due today" board — no time, no
 *  state, nothing for staff to action. */
export interface MedicationEmergencyBadge {
  requirementId: string;
  childId: string;
  medicationName: string;
  emergencyInstructions?: string;
  /** As on MedicationDueSlot — an expired EpiPen stays visible, marked. */
  medicationExpiredOn?: string;
}

// ============================================================================
// Policy & Document Vault
//
// Mirrors src/common/types/policy.ts. Deliberately a copy rather than an
// import: the UI and the Lambda bundle build separately, and a cross-boundary
// import would drag server-only modules into the browser bundle.
// ============================================================================

export enum PolicyStatus {
  /** No row exists yet, or a row with no document. Rendered neutrally. */
  NOT_ADDED = 'NOT_ADDED',
  /** Held on paper. A respectable state, never styled as a failure. */
  PAPER_BASED = 'PAPER_BASED',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

/** What, if anything, a parent is asked to do. Frozen on the VERSION. */
export enum ParentAction {
  NONE = 'NONE',
  VIEW = 'VIEW',
  ACKNOWLEDGE = 'ACKNOWLEDGE',
  SIGN = 'SIGN',
}

export enum PolicyChecklistKey {
  COMPLAINTS = 'COMPLAINTS',
  MEDICATION_ADMINISTRATION = 'MEDICATION_ADMINISTRATION',
  INFECTION_CONTROL = 'INFECTION_CONTROL',
  MANAGING_BEHAVIOUR = 'MANAGING_BEHAVIOUR',
  DROP_OFF_COLLECTION = 'DROP_OFF_COLLECTION',
  STATEMENT_OF_PURPOSE = 'STATEMENT_OF_PURPOSE',
  FIRE_SAFETY = 'FIRE_SAFETY',
  CHILD_SAFEGUARDING_STATEMENT = 'CHILD_SAFEGUARDING_STATEMENT',
  SAFETY_STATEMENT = 'SAFETY_STATEMENT',
}

/** One row of the vault register: a checklist slot or a custom policy. */
export interface PolicyRegisterRow {
  checklistKey?: PolicyChecklistKey;
  title: string;
  status: PolicyStatus;
  /** Absent while NOT_ADDED — there is no row behind the slot yet. */
  policyId?: string;
  currentVersionId?: string;
  reviewDueAt?: string;
  reviewRemindersEnabled: boolean;
  paperNote?: string;
  /** As stored; absent = everyone at the location. */
  audience?: PolicyAudience;
  isCustom: boolean;
}

/**
 * Who a policy is for. Absent on a policy means everyone at the location —
 * the server reads it that way, so the UI must too. `classIds` is never empty.
 */
export type PolicyAudience = { scope: 'LOCATION' } | { scope: 'CLASSES'; classIds: string[] };

export interface Policy {
  policyId: string;
  locationId: string;
  organizationId: string;
  checklistKey?: PolicyChecklistKey;
  title: string;
  summary?: string;
  staffAckRequired: boolean;
  status: PolicyStatus;
  paperNote?: string;
  currentVersionId?: string;
  reviewRemindersEnabled: boolean;
  reviewDueAt?: string;
  lastNudgeAt?: string;
  /** Absent = everyone at the location. */
  audience?: PolicyAudience;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface PolicyVersion {
  versionId: string;
  policyId: string;
  versionNumber: number;
  parentAction: ParentAction;
  effectiveDate: string;
  changeNote?: string;
  materialChange: boolean;
  isSettingsOnly: boolean;
  uploadedBy: string;
  uploadedByName: string;
  createdAt: string;
}

export interface PolicyReviewRecord {
  reviewId: string;
  policyId: string;
  reviewedAt: string;
  reviewedByName: string;
  outcome: 'NO_CHANGE' | 'NEW_VERSION' | 'AUDIENCE_CHANGED';
  versionId?: string;
  note?: string;
  /** Present on AUDIENCE_CHANGED records only. */
  audienceChange?: { from: PolicyAudience; to: PolicyAudience };
}

/** One class a policy is sent to, as coverage names it. */
export type PolicyCoverageAudienceClass =
  | { classId: string; name: string; removed?: undefined }
  | { classId: string; removed: true; name?: undefined };

/** Coverage counts CHILDREN, not guardians — see the design's §5. */
export interface PolicyCoverage {
  version: { versionId: string; parentAction: ParentAction } | null;
  parents: {
    total: number;
    done: number;
    outstandingChildIds: string[];
    outstandingChildren: { childId: string; childName: string }[];
    /** Departed children who signed while enrolled. Absent from a backend older than this field. */
    departedSigned?: number;
    /** Active children outside the chosen classes who signed. Absent from an older backend. */
    signedOutsideAudience?: {
      childId: string;
      firstName: string;
      lastName: string;
      ackId: string;
    }[];
  };
  /** Who the policy is sent to, with class names resolved. Absent from an older backend. */
  audience?: { scope: 'LOCATION' | 'CLASSES'; classes: PolicyCoverageAudienceClass[] };
  staff: { total: number; done: number; outstandingUserIds: string[] };
}

export interface PolicySignatureRow {
  ackId: string;
  audience: 'PARENT' | 'STAFF';
  childId?: string;
  childName?: string;
  childStatus?: 'ACTIVE' | 'LEFT' | 'PENDING_START';
  userId: string;
  userName: string;
  signedName?: string;
  ackType: 'SIGN' | 'ACKNOWLEDGE';
  acknowledgedAt: string;
}

export interface PolicySignatures {
  version: {
    versionId: string;
    versionNumber: number;
    effectiveDate: string;
    parentAction: ParentAction;
  } | null;
  versions: { versionId: string; versionNumber: number; effectiveDate: string }[];
  signatures: PolicySignatureRow[];
  total: number;
}

/** The allowlist projection a parent receives. No file keys, ever. */
export interface ParentSafePolicy {
  policyId: string;
  title: string;
  summary?: string;
  currentVersion: {
    versionId: string;
    versionNumber: number;
    parentAction: ParentAction;
    effectiveDate: string;
    hasChildFriendlyVersion: boolean;
  } | null;
  /** Which of MY children still owe a response. Empty for VIEW-only. */
  outstandingForChildren: { childId: string; childName: string }[];
  /** My children who have responded to the current version — the parent's receipt. Absent from an older backend. */
  signedForChildren?: {
    childId: string;
    childName: string;
    signedByName: string;
    acknowledgedAt: string;
    ackType: 'SIGN' | 'ACKNOWLEDGE';
  }[];
}

/** A staff member's own outstanding-acknowledgement row. */
export interface StaffPolicyRow {
  policyId: string;
  title: string;
  summary?: string;
  currentVersion: { versionId: string; versionNumber: number; effectiveDate: string };
  outstanding: boolean;
}

/** One per-child policy acceptance, as returned by GET /students/:studentId/policies. */
export interface StudentPolicyAcceptance {
  ackId: string;
  policyId: string;
  policyTitle: string;
  policyStatus: PolicyStatus;
  checklistKey?: PolicyChecklistKey;
  versionId: string;
  versionNumber: number;
  effectiveDate: string;
  ackType: 'ACKNOWLEDGE' | 'SIGN';
  signedName?: string;
  acknowledgedByUserId: string;
  acknowledgedByName: string;
  acknowledgedAt: string;
  isCurrent: boolean;
}

// ============================================================================
// Enrolment history — mirrors src/student/handlers/enrollment-history.
// ============================================================================

/** Why an enrolment period closed, when it did not give way to the next one. */
export type EnrollmentEndReason =
  | 'LEFT_EARLY'
  | 'CHILD_LEFT_SERVICE'
  | 'TRANSFERRED'
  | 'CANCELLED_BEFORE_START';

/** Who made an enrolment change, stored on the period at write time. */
export interface EnrollmentActor {
  userId: string;
  name: string;
}

export interface EnrollmentHistoryPeriod {
  /** YYYY-MM-DD, inclusive. */
  from: string;
  /** YYYY-MM-DD, inclusive; absent = open. Before `from` = cancelled before start. */
  to?: string;
  /** 0=Sunday..6=Saturday. Empty means every day the class runs. */
  daysOfWeek: number[];
  setBy?: EnrollmentActor;
  setAt?: string;
  endReason?: EnrollmentEndReason;
  endedBy?: EnrollmentActor;
  endedAt?: string;
  /** Pre-dates change history (no `setBy`, no `setAt`); the UI names no actor. */
  implied?: true;
}

export interface EnrollmentHistoryTerm {
  termId: string;
  termName: string;
  termStartDate: string;
  termEndDate: string;
  /** Newest first. */
  periods: EnrollmentHistoryPeriod[];
}

export interface EnrollmentHistoryClass {
  classId: string;
  className: string;
  /** Newest first. */
  terms: EnrollmentHistoryTerm[];
}

/** GET /students/:studentId/enrollment-history */
export interface EnrollmentHistoryResponse {
  classes: EnrollmentHistoryClass[];
}

// ============================================================================
// Unified Calendar
//
// Mirrors src/calendar/utils/to-client-calendar.ts's `ClientCalendarClassEntry`
// / `ClientCalendarDay` — the handler's ALLOWLIST projection off
// `ResolvedClassDay`, never a raw DynamoDB item. No PK/SK/GSI*/entityType/ttl
// here; see that module's comment about the checklists leak this pattern
// exists to prevent.
//
// `ClassInstanceStatus` and `DayClassStatus` are already declared above
// (mirroring the same backend enums) — reused here rather than redeclared.
// ============================================================================

/** Mirrors src/calendar/utils/resolve-schedule.ts SuppressionReason. */
export type SuppressionReason = 'CANCELLED' | 'CLOSURE';

export interface ClientCalendarClassEntry {
  classId: string;
  className: string;
  startTime: string;
  endTime: string;
  instanceStatus: ClassInstanceStatus;
  suppressedBy: SuppressionReason | null;
  closureLabel?: string;
  overrideReason?: string;
  /** Only present when attendance was derived for this cell (student-scoped views). */
  attendanceStatus?: DayClassStatus;
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
  /** Set only when attendanceStatus is PLANNED_ABSENT — the id to cancel via DELETE /attendance/planned-absence/:absenceId. */
  absenceId?: string;
}

export interface ClientCalendarDay {
  date: string;
  dayOfWeek: number;
  classes: ClientCalendarClassEntry[];
}

/** GET /calendar response body. */
export interface UnifiedCalendarResponse {
  dateRange: { from: string; to: string };
  days: ClientCalendarDay[];
}

// ============================================================================
// Closures
//
// Mirrors src/common/types/closure.ts's `Closure` entity exactly. All
// timestamps are ISO strings, never Date — the DynamoDB DocumentClient can't
// marshal Date, and the service's fromItem/create both return plain fields
// (no PK/SK/GSI* leak here to guard against, unlike the calendar handler).
// ============================================================================

/** Where the closure came from. HOLIDAY rows are seeded; PROVIDER are admin-declared. */
export enum ClosureSource {
  HOLIDAY = 'HOLIDAY',
  PROVIDER = 'PROVIDER',
}

/** LOCATION closes everything; CLASS closes only the listed classIds. */
export enum ClosureScope {
  LOCATION = 'LOCATION',
  CLASS = 'CLASS',
}

/**
 * CLOSED suppresses the day. OPEN_AS_NORMAL is a holiday the location decided
 * to open for. OPEN_MODIFIED means open with different hours or classes; the
 * actual time change rides on an ordinary ClassOverride MODIFIED row.
 */
export enum ClosureStatus {
  CLOSED = 'CLOSED',
  OPEN_AS_NORMAL = 'OPEN_AS_NORMAL',
  OPEN_MODIFIED = 'OPEN_MODIFIED',
}

/** A closed period at a location. A date range is ONE row, not one per day. */
export interface Closure {
  closureId: string;
  locationId: string;
  organizationId: string;
  label: string;
  note?: string;
  /** YYYY-MM-DD. Inclusive. */
  startDate: string;
  /** YYYY-MM-DD. Inclusive. Equals startDate for a single day. */
  endDate: string;
  source: ClosureSource;
  scope: ClosureScope;
  /** Only present when scope === CLASS. */
  classIds?: string[];
  status: ClosureStatus;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedBy?: string;
  updatedByName?: string;
  updatedAt: string;
}

/** Mirrors src/closures/utils/impact.ts ClosureImpact — the read-only preview shown before a closure is confirmed. */
export interface ClosureImpact {
  /** Number of running class-occurrences in the range that would be suppressed. */
  sessions: number;
  /** Distinct children expected across those occurrences. */
  expectedChildren: number;
  /** Planned absences already filed for one of those occurrences. */
  absencesFiled: number;
  /** Distinct families (by parent userId) who would need notifying. */
  affectedFamilies: number;
  affectedParentUserIds: string[];
}

// ============================================================================
// Daily Checklists
//
// Mirrors src/common/types/daily-checklist.ts and
// src/daily-checklists/utils/to-client-run.ts (ClientChecklistRun).
// Deliberately a copy rather than an import: the UI and the Lambda bundle
// build separately, and a cross-boundary import would drag server-only
// modules into the browser bundle.
//
// NOT a statutory record — an operational tool with an audit trail, never
// labelled Tusla-required.
// ============================================================================

export enum ChecklistKind {
  OPENING = 'OPENING',
  CLOSING = 'CLOSING',
}

export enum ChecklistAnswerValue {
  YES = 'YES',
  NO = 'NO',
  /** The item genuinely did not apply today — distinct from unanswered. */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export type ChecklistRunStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE' | 'SIGNED_OFF';

export interface ChecklistEditEntry {
  editedAt: string;
  editedBy: string;
  editedByName: string;
  fields: string[];
}

/** The admin-defined template row. */
export interface ChecklistItem {
  itemId: string;
  locationId: string;
  organizationId: string;
  kind: ChecklistKind;
  label: string;
  order: number;
  /** Must be answered before a run can be signed off. */
  isRequired: boolean;
  /** Soft-delete only — historical runs must stay readable. */
  isActive: boolean;
  editHistory: ChecklistEditEntry[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

/** The frozen copy of an item held on a run, snapshotted at run creation so a
 *  signed run never re-resolves against the live template. */
export interface ChecklistItemSnapshot {
  itemId: string;
  label: string;
  order: number;
  isRequired: boolean;
}

export interface ChecklistAnswer {
  itemId: string;
  value: ChecklistAnswerValue;
  /** Required when value is NO. */
  comment?: string;
  answeredAt: string;
  answeredBy: string;
  answeredByName: string;
}

/**
 * The allowlist projection a client receives for a run — mirrors
 * ClientChecklistRun exactly. No entityType, PK/SK, GSI attributes, or ttl:
 * those are storage-only fields the server never sends.
 */
export interface ClientChecklistRun {
  runId: string;
  locationId: string;
  organizationId: string;
  /** YYYY-MM-DD in EUROPE/DUBLIN. */
  date: string;
  kind: ChecklistKind;
  items: ChecklistItemSnapshot[];
  answers: ChecklistAnswer[];
  startedAt: string;
  startedBy: string;
  startedByName: string;
  status: ChecklistRunStatus;
  createdAt: string;
  updatedAt: string;
  signedOffAt?: string;
  signedOffBy?: string;
  signedOffByName?: string;
  /** Free-text note for the manager; also the explanation for any No. */
  note?: string;
}

// ============================================================================
// Staff Compliance (Garda Vetting & Credentials)
//
// Mirrors src/common/types/staff-compliance.ts and the allowlist projections
// in src/staff-compliance/utils/to-client.ts (toClientVetting,
// toClientCredential, toClientReference). Deliberately a copy rather than an
// import: the UI and the Lambda bundle build separately, and a cross-boundary
// import would drag server-only modules into the browser bundle.
//
// No entityType, PK/SK, GSI attributes, or ttl anywhere below — those are
// storage-only fields the allowlist projection never sends to a client.
//
// Record-keeping only: nothing here ever blocks a login, session, or class
// assignment.
// ============================================================================

/**
 * NOT_RECORDED and REVET_DUE are DERIVED — never stored — but a read can
 * return either of them (get-profile / list-register compute this fresh on
 * every request). Only IN_PROGRESS and VETTED are ever written by a client;
 * see WritableVettingStatus below for the narrower type writes must use.
 */
export enum VettingStatus {
  NOT_RECORDED = 'NOT_RECORDED',
  IN_PROGRESS = 'IN_PROGRESS',
  VETTED = 'VETTED',
  REVET_DUE = 'REVET_DUE',
}

/** Only these two are ever accepted by the server on a write (upsert-vetting). */
export type WritableVettingStatus = VettingStatus.IN_PROGRESS | VettingStatus.VETTED;

export enum CredentialType {
  FIRST_AID = 'FIRST_AID',
  CHILD_PROTECTION = 'CHILD_PROTECTION',
  QUALIFICATION = 'QUALIFICATION',
  FIRE_SAFETY = 'FIRE_SAFETY',
  MANUAL_HANDLING = 'MANUAL_HANDLING',
  OTHER = 'OTHER',
}

/** Mirrors toClientVetting exactly. */
export interface ClientVettingRecord {
  vettingId: string;
  userId: string;
  organizationId: string;
  staffNameSnapshot: string;
  locationIdsSnapshot: string[];
  /** Derived on read — can be any of the four members, not just the writable two. */
  vettingStatus: VettingStatus;
  disclosureDate?: string;
  nvbReference?: string;
  vettedThrough?: string;
  /** The only outcome ever stored — disclosure content is never persisted. */
  decision?: 'SATISFACTORY';
  decidedBy?: string;
  decidedByName?: string;
  decidedAt?: string;
  revetDueAt?: string;
  revetDueOverridden: boolean;
  attachmentS3Key?: string;
  isActive: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors toClientCredential exactly. */
export interface ClientCredentialRecord {
  credentialId: string;
  userId: string;
  organizationId: string;
  staffNameSnapshot: string;
  locationIdsSnapshot: string[];
  /** A location can name a credential type the enum doesn't cover. */
  credentialType: CredentialType | string;
  title: string;
  issuer?: string;
  awardedDate?: string;
  /** Absent for qualifications, which do not expire. */
  expiryDate?: string;
  attachmentS3Key?: string;
  verified: boolean;
  verifiedBy?: string;
  verifiedByName?: string;
  verifiedAt?: string;
  submittedBySelf: boolean;
  isActive: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors toClientReference exactly. */
export interface ClientReferenceCheck {
  referenceId: string;
  userId: string;
  organizationId: string;
  staffNameSnapshot: string;
  locationIdsSnapshot: string[];
  refereeName: string;
  refereeOrganisation?: string;
  validatedAt: string;
  validatedBy: string;
  validatedByName: string;
  note?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** GET /staff/{staffUserId}/compliance response body (get-profile handler). */
export interface StaffComplianceProfile {
  /** Whose records these are — present even when the profile holds no records
   *  yet (the per-record staffNameSnapshot only exists after the first one).
   *  Null when the user record is gone; optional so a UI deployed ahead of
   *  the backend renders the snapshot fallback instead of crashing. */
  staff?: { id: string; firstName: string; lastName: string; email: string } | null;
  vettingStatus: VettingStatus;
  vetting: ClientVettingRecord[];
  credentials: ClientCredentialRecord[];
  references: ClientReferenceCheck[];
}

/**
 * One row of the location register — one row PER PERSON, not per record (see
 * list-register handler: vetting/credentials/references for the same person
 * are grouped before this shape is built). Not a toClient* allowlist output;
 * built inline by the handler.
 */
export interface StaffComplianceRegisterEntry {
  userId: string;
  staffNameSnapshot: string;
  vettingStatus: VettingStatus;
  credentialCount: number;
  unverifiedCredentialCount: number;
  referenceCount: number;
}

/**
 * Per-location config: which credential types this location expects, and
 * whether it expects Garda vetting at all. Mirrors ExpectedItemsConfig — this
 * type has no storage attributes of its own, so manage-expected-items returns
 * it directly rather than through a toClient* projection.
 *
 * `updatedBy`/`updatedAt` are absent on the neutral default the GET endpoint
 * returns for a location that has never configured expected items.
 */
export interface StaffExpectedItemsConfig {
  locationId: string;
  organizationId: string;
  expectsVetting: boolean;
  expectedCredentialTypes: string[];
  updatedBy?: string;
  updatedAt?: string;
}

/**
 * One parent/guardian linked to a child, as returned by
 * `GET /students/{studentId}/parents` for staff and admins.
 *
 * Hand-mirrored from the handler's response shape — `ui/` is a separate TS
 * project and must not import server types.
 *
 * `phone` and `address` are ABSENT (not empty strings) when the parent has
 * not recorded them, so the UI can distinguish "not recorded" from a blank.
 * `canPickup` is deliberately not part of this shape: it exists on the
 * relationship row but no parent can set it, so surfacing it would mislead.
 */
export interface ParentSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  relationship: string;
  isPrimary: boolean;
  emergencyContact: boolean;
}

/**
 * What linking a parent to a child produced. `emailAction` is the fact the UI
 * reports back: 'signup-invite' = they had no account and got a signup link;
 * 'notification' = an existing account was linked and told; 'skipped' = a
 * signup invite for this child was already pending, nothing re-sent;
 * 'email-failed' = linked, but the email could not be sent — show an error.
 */
export type ParentLinkEmailAction = 'signup-invite' | 'notification' | 'skipped' | 'email-failed';

/** Mirrors the backend `ParentRelationship` enum (what the link endpoints accept). */
export type ParentRelationship =
  | 'MOTHER'
  | 'FATHER'
  | 'GUARDIAN'
  | 'GRANDPARENT'
  | 'STEPMOTHER'
  | 'STEPFATHER'
  | 'OTHER';

/** The linked person as BOTH link endpoints return them. */
export interface ParentLinkResult {
  id: string;
  email: string;
  name: string;
  relationship: string;
  emailAction: ParentLinkEmailAction;
}

export interface CoparentSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  relationship: string;
  isPrimary: boolean;
  canPickup: boolean;
  emergencyContact: boolean;
}
